import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../ResumePage.css';

const RESUME_PDF = '/assets/JOSHUA_ZHANG_Resume.pdf';

const contactLinks = [
    { label: 'joshuazhang447@gmail.com', href: 'mailto:joshuazhang447@gmail.com' },
    { label: 'joshuazhang447.dev', href: 'https://joshuazhang447.dev/' },
    { label: 'github.com/joshuazhang447', href: 'https://github.com/joshuazhang447' },
];

const skills = [
    { label: 'Languages', value: 'PHP, TypeScript, JavaScript, SQL, Python, C#, HTML/CSS, Kotlin, Swift' },
    { label: 'Frameworks', value: 'Laravel, React, React Native (Expo), Angular, Electron, ASP.NET, Astro, Tailwind CSS, Bootstrap' },
    { label: 'Servers & DevOps', value: 'Linux (Ubuntu, LAMP), Windows Server (IIS), Nginx, Docker, SSH, GitHub Actions (CI/CD), MySQL, PostgreSQL, Supabase' },
    { label: 'APIs & Tools', value: 'Stripe, Google Maps Platform, Cloudflare, FFmpeg, Git, GitHub, Claude Code (agents, skills, MCP)' },
    { label: 'Spoken languages', value: 'English, Mandarin Chinese, French (basic)' },
];

const experience = [
    {
        title: 'Web Developer (Internship)',
        date: '04/2026 – 10/2026',
        org: 'YellowDucky, local tech startup',
        location: 'Ottawa, ON',
        link: { label: 'yellowducky.ca', href: 'https://yellowducky.ca/' },
        blurb: 'A Chinese-language online marketplace serving the Chinese community of Ottawa–Gatineau.',
        bullets: [
            'Launched the site as sole developer of a 2-person team, shipping 133 peer-reviewed Laravel/PHP pull requests.',
            "Ran 30+ Zoom interviews with Ottawa's Chinese community and users of rivals 51Canada and OttawaZine.",
            'Rebuilt Chinese search: MySQL ngram indexes, OpenCC Traditional→Simplified, 41,547-word synonym dictionary.',
            'Built tiered Stripe VIP memberships (Laravel Cashier, 4 CAD tiers) and fixed a double-billing race.',
            'Built an async FFmpeg pipeline on Laravel queues converting 70 MB iPhone uploads to streamable H.264.',
            'Implemented Chinese-first SEO (meta tags, JSON-LD, canonical URLs, sitemaps) with Google Search Console.',
            "Deployed and ran production over SSH: Ubuntu, Nginx, PHP-FPM, Let's Encrypt HTTPS, DNS and mail records, and GitHub Actions CI/CD.",
            'Contained a PHP CVE within 12 hours: took Nginx offline, audited for compromise, patched 13 PHP packages.',
        ],
    },
    {
        title: 'Lead Developer (Volunteer)',
        date: '09/2026 – Present',
        org: 'One Among Us, East Asian transgender support organization',
        location: 'Toronto, ON (Remote)',
        link: { label: 'lavenderbook.oau.app', href: 'https://lavenderbook.oau.app/' },
        blurb: 'LavenderBook, an LGBTQ+ app for rating venues and finding clinics, shelters and services.',
        bullets: [
            'Built it with React Native, Expo and TypeScript for Android and web, on Supabase and Google Places.',
            'Presented a security review to 5 OAU tech staff, then built a 7-point plan against re-identification and data scraping for users in high-risk countries.',
            'Shipped per-review pseudonyms, week-only dates, 250 m on-device location rounding, a "Leave now" exit.',
            'Enforced every permission in PostgreSQL (row-level security, column grants) across 31 migrations.',
            'Kept the Places API key off clients via Deno edge functions; built a moderation queue for slurs.',
            "Deployed the web app on Cloudflare and set up DNS for OAU's domain and subdomain; support a second developer on iOS.",
        ],
    },
];

const projects = [
    {
        title: 'Circle 2 Lab Publisher (Capstone)',
        stack: 'Electron, React, Angular',
        date: '02/2026 – 04/2026',
        org: 'Circle 2 Lab, Norwegian indie game publisher',
        link: { label: 'circle2.moe', href: 'https://circle2.moe/' },
        blurb: "A news and publishing platform for the studio's anime-style games, with a desktop posting app.",
        bullets: [
            "Built the team's Electron, React and TypeScript app that publishes Editor.js posts over FTP, no backend.",
            'Made FTP sync reliable: one transfer queue, keep-alive, auto-reconnect and a patched basic-ftp crash.',
            'Wrote the Angular 21 site\'s post renderer and SEO; circle2.moe ranks first on Google for "Circle 2 Lab".',
        ],
    },
];

const Section = ({ title, children }) => (
    <section className="resume-section">
        <h2 className="resume-section-title">{title}</h2>
        {children}
    </section>
);

// One resume entry: bold title and date, italic org and location, an optional link line, then bullets
const Entry = ({ title, stack, date, org, location, link, blurb, bullets }) => (
    <div className="resume-entry">
        <div className="entry-row">
            <span className="entry-title">
                {title}
                {stack && <><span className="entry-sep">|</span><em className="entry-stack">{stack}</em></>}
            </span>
            <span className="entry-date">{date}</span>
        </div>
        <div className="entry-row entry-sub">
            <em>{org}</em>
            {location && <em>{location}</em>}
        </div>
        {link && (
            <p className="entry-blurb">
                <a href={link.href} target="_blank" rel="noopener noreferrer">{link.label}</a>
                <span className="entry-sep">|</span>
                {blurb}
            </p>
        )}
        <ul className="resume-list">
            {bullets.map((b) => <li key={b}>{b}</li>)}
        </ul>
    </div>
);

const ResumePage = () => {
    return (
        <div className="resume-page-container">
            {/* Header */}
            <header className="resume-header">
                <div className="resume-header-left">
                    <Link to="/" className="resume-btn" style={{ textDecoration: 'none' }}>
                        &larr; Home
                    </Link>
                </div>
                <div className="resume-header-right">
                    {/* A plain link that opens the PDF: mobile browsers (in-app ones
                        especially) can ignore `download` and block new tabs, so the tap
                        did nothing. Opening the PDF itself always works, and it can be
                        saved from the browser's PDF viewer. */}
                    <a href={RESUME_PDF} className="resume-btn">
                        Download PDF
                    </a>
                </div>
            </header>

            {/* Resume Content */}
            <motion.div
                className="resume-document"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
            >
                {/* Name / Contact Header */}
                <div className="resume-top-section">
                    <h1 className="resume-name">Joshua Zhang</h1>
                    {/* Two groups so a narrow screen breaks the line cleanly instead of orphaning a separator */}
                    <div className="resume-contact-info">
                        <span className="contact-group">
                            <span>Gatineau, QC</span>
                            <a href="tel:+12267591840">(226) 759-1840</a>
                            <a href={contactLinks[0].href}>{contactLinks[0].label}</a>
                        </span>
                        <span className="contact-group">
                            {contactLinks.slice(1).map((c) => (
                                <a key={c.href} href={c.href} target="_blank" rel="noopener noreferrer">{c.label}</a>
                            ))}
                        </span>
                    </div>
                </div>

                <Section title="Summary">
                    <p className="resume-summary">
                        Full-stack developer who launched a Chinese-language marketplace as its sole developer (133 reviewed pull requests)
                        and leads a security-focused LGBTQ+ app in React Native. Computer Programming diploma with a 4.0 GPA.
                    </p>
                </Section>

                <Section title="Technical Skills">
                    <dl className="skills-list">
                        {skills.map((s) => (
                            <div className="skills-row" key={s.label}>
                                <dt>{s.label}:</dt>
                                <dd>{s.value}</dd>
                            </div>
                        ))}
                    </dl>
                </Section>

                <Section title="Education">
                    <Entry
                        title="St. Clair College"
                        date="09/2024 – 04/2026"
                        org="Ontario College Diploma, Computer Programming"
                        location="Windsor, ON"
                        bullets={['GPA 4.0/4.0; Academic Excellence Award in all 4 semesters; Student Leadership Award']}
                    />
                </Section>

                <Section title="Experience">
                    {experience.map((e) => <Entry key={e.title} {...e} />)}
                </Section>

                <Section title="Projects">
                    {projects.map((p) => <Entry key={p.title} {...p} />)}
                </Section>
            </motion.div>
        </div>
    );
};

export default ResumePage;
